import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { useCallback, useEffect, useMemo } from "react";
import { FormMoneyMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_money_masked_input";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";
import { EntradaProdutoForm, EntradaProdutoSchema } from "../../../schemas/AvaliacaoFornecedor_cadastro/entrada_produtos";
import { DropDownFornecedor } from "../../../../../../common/components/dropdown_fornecedor";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import { GenerateEntradaProdutosMultiColumns } from "../columns/entrada_produtos_multi";
import { EntradaProduto } from "../../../../../controleEstoque/domain/models/entrada_produtos_lista";
import { AvaliacaoOrdemCompra } from "../../../../domain/models/avaliacao_ordem_compra";

import dayjs from "dayjs";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  avaliacaoOrdemCompra: AvaliacaoOrdemCompra | null;
  selectedEntradaProduto: EntradaProduto | undefined;
  entradaProduto: EntradaProduto[] | undefined;
  centroCustoLista: CentroCustoDropDown[];
  onCreateCentroCusto: (onCreated: (idCentroCusto: number) => void) => void;
  onClose: () => void;
  onSubmit: (e: EntradaProdutoForm) => void | Promise<void>;
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void;
};

export const EntradaProdutoMultiModal = ({
  open,
  saving,
  loading,
  avaliacaoOrdemCompra,
  entradaProduto,
  selectedEntradaProduto,
  centroCustoLista,
  onCreateCentroCusto,
  onClose,
  onSubmit,
  onOpenChangeHistory,
}: Props) => {
  const hookForm = useForm<EntradaProdutoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(EntradaProdutoSchema),
  });

  useEffect(() => {
    if (!open) return;

    hookForm.reset({
      Data: avaliacaoOrdemCompra?.data
        ? dayjs.utc(avaliacaoOrdemCompra.data).local().toDate()
        : new Date(),
      IdCentroCusto: avaliacaoOrdemCompra?.idCentroCusto ?? null,
      IdFornecedor: avaliacaoOrdemCompra?.idFornecedor ?? null,
      NrNotaFiscal: avaliacaoOrdemCompra?.nrNotaFiscal ?? null,
      ValorTotal: avaliacaoOrdemCompra?.valorTotal ?? null,
    });
  }, [open, avaliacaoOrdemCompra, hookForm]);

  const handleChangeHistory = useCallback(() => {
    if (selectedEntradaProduto) {
      onOpenChangeHistory(
        selectedEntradaProduto.IdEntradaProduto,
        LogTipoTabela.ENTRADA_PRODUTO,
      );
    }
  }, [selectedEntradaProduto, onOpenChangeHistory]);

  const columnsDef = useMemo(() => {
    return GenerateEntradaProdutosMultiColumns({});
  }, [
  ]);

  return (
    <Dialog
      open={open}
      maxWidth='xl'
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(onSubmit)
        }
      }}
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "none",
          width: "98vw",
          margin: "16px"
        }
      }}>
      <DialogTitle>Entrada de produtos entregues no estoque</DialogTitle>

      <DialogContent>
        {avaliacaoOrdemCompra && (
          <div className="mb-2 text-sm text-gray-600 dark:text-gray-200">
            <span className="font-semibold">Ordem de compra: </span>
            {avaliacaoOrdemCompra.nrOrdemCompra ?? "—"}
          </div>
        )}

        <FormContainer title="Cadastrar/Editar" arrangement="fullRow" paddingTop="small" paddingBottom="small">
          <div className="flex flex-col gap-y-2 w-full pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <FormDatePickerInput
                name="Data"
                label="Data"
                reactHookForm={hookForm}
                readOnly={true}
              />

              <DropDownFornecedor
                name="IdFornecedor"
                label="Fornecedor"
                reactHookForm={hookForm}
                readOnly={true}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <DefaultFormAutocomplete
                name="IdCentroCusto"
                label="Centro de Custo"
                newButtonLabel="Novo"
                getOptionLabel={(x) => x.title}
                getOptionKey={(x) => x.id}
                options={centroCustoLista}
                required={true}
                reactHookForm={hookForm}
                onNew={() => onCreateCentroCusto(idCentroCusto => {
                  hookForm.setValue("IdCentroCusto", idCentroCusto, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                })}
              />

              <DefaultFormInput
                name="NrNotaFiscal"
                label="N° Nota Fiscal"
                readOnly={true}
                reactHookForm={hookForm}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <FormMoneyMaskedInput
                name="ValorTotal"
                label="Valor Total(R$)"
                largeNumbers={true}
                readOnly={true}
                reactHookForm={hookForm}
              />
            </div>
          </div>
        </FormContainer>

        <div style={{ width: "100%", marginTop: "24px" }}>
          <StyledDataGrid
            tableName="reajuste_cadastro_lista"
            disableColumnMenu={false}
            showToolbar={true}
            height="30rem"
            loading={loading}
            disableRowSelectionOnClick={true}
            getRowId={(row: EntradaProduto) => row.IdEntradaProduto}
            getRowHeight={() => 'auto'}
            columns={columnsDef}
            textSize="pequeno"
            checkboxSelection={false}
            rows={entradaProduto ?? []}
            rowCount={entradaProduto ? entradaProduto.length : 0}
            pageSizeOptions={[10, 25, 50, 100]}
            paginationMode="server"
          />
        </div>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {isNotNullOrUndefined(selectedEntradaProduto) && (
              <PrimaryButton buttonColor="yellow" onClick={handleChangeHistory}>
                <span className="text-xs">Historico de alterações</span>
              </PrimaryButton>
            )}
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={onClose}
              >
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton type="button" disabled buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  disabled={loading}
                  buttonColor="primary-green"
                >
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};
